/**
 * Role Context
 * Manages user role switching (Admin, Player, Referee)
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isAdminRole as checkIsAdminRole, isSuperAdmin as checkIsSuperAdmin } from '../constants/roles';

const RoleContext = createContext();

export function useRole() {
  return useContext(RoleContext);
}

export function RoleProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const [currentRole, setCurrentRole] = useState('player');
  const [availableRoles, setAvailableRoles] = useState([]);

  // Determine available roles for the user
  useEffect(() => {
    if (!currentUser || !userProfile) {
      setAvailableRoles([]);
      setCurrentRole('player');
      return;
    }

    async function determineRoles() {
      const roles = [];
      const profileForCheck = { ...userProfile, email: userProfile.email || currentUser?.email };
      const userIsAdmin = checkIsAdminRole(profileForCheck);
      const userIsSuperAdmin = checkIsSuperAdmin(profileForCheck);

      // Player role - available if onboarded, or if admin/super admin (so they can switch to player view)
      if (userProfile.onboarded || userIsAdmin || userIsSuperAdmin) {
        roles.push({ id: 'player', name: 'Player', icon: '🏓' });
      }

      // Admin / Super Admin role - available if user is admin or super admin (so they can switch to Player etc.)
      if (userIsAdmin || userIsSuperAdmin) {
        roles.push({
          id: 'admin',
          name: userIsSuperAdmin ? 'Super Admin' : 'Admin',
          icon: userIsSuperAdmin ? '⚙️' : '👑'
        });
      }
      
      // Venue manager role - available if user is venue manager
      if (userProfile.isVenueManager) {
        roles.push({ id: 'venueManager', name: 'Venue Manager', icon: '🏟️' });
      }
      
      // Referee role - check if user is assigned as referee in any active match
      const isReferee = await checkRefereeRole();
      if (isReferee) {
        roles.push({ id: 'referee', name: 'Referee', icon: '⚖️' });
      }
      
      setAvailableRoles(roles);
      
      // Set default role if current role is not available
      if (roles.length > 0 && !roles.find(r => r.id === currentRole)) {
        const savedRole = localStorage.getItem('currentRole');
        if (savedRole && roles.find(r => r.id === savedRole)) {
          setCurrentRole(savedRole);
        } else {
          setCurrentRole(roles[0].id);
        }
      }
    }
    
    determineRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile]);

  async function checkRefereeRole() {
    if (!currentUser) return false;
    
    try {
      const matchesRef = collection(db, 'matches');
      const q = query(
        matchesRef,
        where('refereeId', '==', currentUser.uid),
        where('status', 'in', ['scheduled', 'in-progress'])
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking referee role:', error);
      return false;
    }
  }

  function switchRole(roleId) {
    if (availableRoles.find(r => r.id === roleId)) {
      setCurrentRole(roleId);
      // Store in localStorage for persistence
      localStorage.setItem('currentRole', roleId);
    }
  }

  // Load role from localStorage when available roles change
  useEffect(() => {
    if (availableRoles.length > 0) {
      const savedRole = localStorage.getItem('currentRole');
      if (savedRole && availableRoles.find(r => r.id === savedRole)) {
        setCurrentRole(savedRole);
      } else if (!availableRoles.find(r => r.id === currentRole)) {
        // If current role is not available, switch to first available
        setCurrentRole(availableRoles[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRoles]);

  const profileForCheck = userProfile ? { ...userProfile, email: userProfile.email || currentUser?.email } : null;
  const hasAdminCapability = profileForCheck ? checkIsAdminRole(profileForCheck) : false;
  const userIsSuperAdmin = profileForCheck ? checkIsSuperAdmin(profileForCheck) : false;
  // Super admin must always have admin tab and all admin controls (e.g. when identified by email only)
  const canAccessAdminPanel = hasAdminCapability || userIsSuperAdmin;

  const value = {
    currentRole,
    availableRoles,
    switchRole,
    isAdmin: currentRole === 'admin' && (hasAdminCapability || userIsSuperAdmin),
    canAccessAdmin: canAccessAdminPanel,
    isSuperAdmin: userIsSuperAdmin,
    isVenueManager: (currentRole === 'venueManager' && userProfile?.isVenueManager) || userProfile?.isVenueManager,
    isPlayer: currentRole === 'player',
    isReferee: currentRole === 'referee'
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
}

